import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  customerListGasStations,
  customerListProducts,
  customerCreateOrder,
  type CustomerGasStationResponse,
  type CustomerProductResponse,
  type ErrorResponse,
} from '@vn-propane/shared';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageSelector } from '@/components/ui/language-selector';
import {
  Loader2,
  MapPin,
  Package,
  ShoppingCart,
  Plus,
  Minus,
  ArrowLeft,
  CheckCircle2,
  Trash2,
  Banknote,
  CreditCard,
  CircleHelp,
  ImageIcon,
} from 'lucide-react';
import type { PaymentMethod } from '@vn-propane/shared';
import { getImageUrl } from '@/lib/utils';

type CartItem = {
  product: CustomerProductResponse;
  quantity: number;
};

export default function CreateOrderPage() {
  const { t, i18n } = useTranslation(['orders', 'common', 'errors']);
  const navigate = useNavigate();

  // State
  const [gasStations, setGasStations] = useState<CustomerGasStationResponse[]>([]);
  const [selectedStation, setSelectedStation] = useState<CustomerGasStationResponse | null>(null);
  const [products, setProducts] = useState<CustomerProductResponse[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('online');

  // Loading states
  const [isLoadingStations, setIsLoadingStations] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Result states
  const [error, setError] = useState<string | null>(null);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);

  // Load gas stations on mount
  useEffect(() => {
    async function loadGasStations() {
      try {
        const result = await customerListGasStations();
        if (result.data?.data) {
          setGasStations(result.data.data);
        } else if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          if (errorResponse.error?.code === '401') {
            navigate('/login');
            return;
          }
          setError(errorResponse.error?.message || t('errors:unexpectedError'));
        }
      } catch {
        setError(t('errors:unexpectedError'));
      } finally {
        setIsLoadingStations(false);
      }
    }

    loadGasStations();
  }, [navigate, t]);

  // Load products when station is selected
  useEffect(() => {
    async function loadProducts() {
      if (!selectedStation) {
        setProducts([]);
        return;
      }

      setIsLoadingProducts(true);
      setError(null);

      try {
        const result = await customerListProducts({
          path: { gas_station_id: selectedStation.id },
        });
        if (result.data?.data) {
          setProducts(result.data.data);
        } else if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          setError(errorResponse.error?.message || t('errors:unexpectedError'));
        }
      } catch {
        setError(t('errors:unexpectedError'));
      } finally {
        setIsLoadingProducts(false);
      }
    }

    loadProducts();
    // Clear cart when changing stations
    setCart([]);
  }, [selectedStation, t]);

  const handleStationSelect = (station: CustomerGasStationResponse) => {
    setSelectedStation(station);
    setError(null);
  };

  const addToCart = (product: CustomerProductResponse) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: Math.min(item.quantity + 1, product.availableQuantity) }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.product.id === productId) {
            const newQuantity = item.quantity + delta;
            if (newQuantity <= 0) return null;
            return {
              ...item,
              quantity: Math.min(newQuantity, item.product.availableQuantity),
            };
          }
          return item;
        })
        .filter(Boolean) as CartItem[]
    );
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const handleSubmit = async () => {
    if (!selectedStation || cart.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await customerCreateOrder({
        body: {
          gasStationId: selectedStation.id,
          items: cart.map((item) => ({
            product_id: item.product.id,
            quantity: item.quantity,
          })),
          notes: notes || undefined,
          paymentMethod: paymentMethod,
        },
      });

      if (result.data?.data) {
        setCreatedOrderId(result.data.data.id);
      } else if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('create.orderFailed'));
      }
    } catch {
      setError(t('create.orderFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (createdOrderId) {
    return (
      <div className="min-h-screen bg-muted/40 relative">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <a
            href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t('common:navigation.help')}
          >
            <CircleHelp className="h-5 w-5" />
          </a>
          <LanguageSelector />
        </div>

        <div className="max-w-2xl mx-auto p-4 pt-16">
          <div className="bg-background rounded-lg border shadow-sm p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold tracking-tight mb-2">{t('create.orderSuccess')}</h1>
            <div className="flex flex-col gap-3 mt-6">
              <Button onClick={() => navigate(`/orders/${createdOrderId}`)}>
                {t('create.viewOrder')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/orders')}>
                {t('create.backToOrders')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <a
          href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={t('common:navigation.help')}
        >
          <CircleHelp className="h-5 w-5" />
        </a>
        <LanguageSelector />
      </div>

      <div className="max-w-2xl mx-auto p-4 pt-16">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-extrabold tracking-tight">{t('create.title')}</h1>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Select Gas Station */}
        <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('create.selectGasStation')}
          </h2>

          {isLoadingStations ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">{t('create.loadingGasStations')}</span>
            </div>
          ) : gasStations.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">{t('create.noGasStations')}</p>
          ) : (
            <div className="space-y-2">
              {gasStations.map((station) => (
                <button
                  key={station.id}
                  onClick={() => handleStationSelect(station)}
                  className={`w-full p-4 rounded-lg border text-left transition-colors ${
                    selectedStation?.id === station.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <p className="font-medium">{station.name}</p>
                  {station.address && (
                    <p className="text-sm text-muted-foreground mt-1">{station.address}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Step 2: Select Products */}
        {selectedStation && (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t('create.selectProducts')}
            </h2>

            {isLoadingProducts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">{t('create.loadingProducts')}</span>
              </div>
            ) : products.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">{t('create.noProducts')}</p>
            ) : (
              <div className="space-y-3">
                {products.map((product) => {
                  const cartItem = cart.find((item) => item.product.id === product.id);
                  const imageSrc = getImageUrl(product.imageUrl);
                  return (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-4 border rounded-lg gap-3"
                    >
                      <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt=""
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(product.price)} {t('create.per')} {product.unit}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {product.availableQuantity} {t('create.available')}
                        </p>
                      </div>

                      {cartItem ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(product.id, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{cartItem.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(product.id, 1)}
                            disabled={cartItem.quantity >= product.availableQuantity}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addToCart(product)}
                          disabled={product.availableQuantity <= 0}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('create.addToCart')}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Cart Summary */}
        {cart.length > 0 && (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              {t('orderItems')}
            </h2>

            <div className="space-y-3">
              {cart.map((item) => {
                const imageSrc = getImageUrl(item.product.imageUrl);
                return (
                  <div
                    key={item.product.id}
                    className="flex items-center justify-between py-2 border-b last:border-0 gap-3"
                  >
                    <div className="w-10 h-10 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt=""
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.product.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.quantity} x {formatCurrency(item.product.price)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-medium">
                        {formatCurrency(item.product.price * item.quantity)}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => removeFromCart(item.product.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <span className="text-lg font-semibold">{t('total')}</span>
              <span className="text-xl font-extrabold tabular-nums text-primary">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>
        )}

        {/* Payment Method */}
        {selectedStation && cart.length > 0 && (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {t('paymentMethod')}
            </h2>
            <div className="space-y-2">
              <button
                onClick={() => setPaymentMethod('online')}
                className={`w-full p-4 rounded-lg border text-left transition-colors flex items-center gap-3 ${
                  paymentMethod === 'online'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <CreditCard className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="font-medium">{t('onlinePayment')}</p>
                  <p className="text-sm text-muted-foreground">{t('onlinePaymentDescription')}</p>
                </div>
              </button>
              <button
                onClick={() => setPaymentMethod('cash')}
                className={`w-full p-4 rounded-lg border text-left transition-colors flex items-center gap-3 ${
                  paymentMethod === 'cash'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <Banknote className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="font-medium">{t('cashOnDelivery')}</p>
                  <p className="text-sm text-muted-foreground">{t('cashOnDeliveryDescription')}</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Notes */}
        {selectedStation && (
          <div className="bg-background rounded-lg border shadow-sm p-6 mb-4">
            <h2 className="text-lg font-semibold mb-3">{t('create.addNotes')}</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('create.notesPlaceholder')}
              className="w-full p-3 border rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        )}

        {/* Submit Button */}
        {selectedStation && (
          <Button
            className="w-full"
            size="lg"
            onClick={handleSubmit}
            disabled={cart.length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('create.placingOrder')}
              </>
            ) : (
              t('create.placeOrder')
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
